import React from 'react';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextareaAutosize,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import * as Widgets from './Index';

function asArray(value) {
  return Array.isArray(value) ? value : [];
}

function hasOwn(obj, key) {
  return !!obj && Object.prototype.hasOwnProperty.call(obj, key);
}

function isBlank(value) {
  return value === null || value === undefined || (typeof value === 'string' && value.trim() === '');
}

function formatPreviewValue(value) {
  if (value === null) return 'null';
  if (value === undefined) return 'undefined';
  if (typeof value === 'string') return value.length > 40 ? `${value.slice(0, 40)}...` : value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (Array.isArray(value)) return `Array(${value.length})`;
  if (typeof value === 'object') return 'Object';
  return String(value);
}

function isPlainObject(value) {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function collectPathsFromObject(obj, prefix = '', output = [], depth = 0) {
  if (!isPlainObject(obj) || depth > 5) return output;

  Object.entries(obj).forEach(([key, value]) => {
    if (isBlank(key)) return;

    const path = prefix ? `${prefix}/${key}` : key;

    if (isPlainObject(value)) {
      output.push({ path, preview: formatPreviewValue(value), type: 'object' });
      collectPathsFromObject(value, path, output, depth + 1);
      return;
    }

    if (Array.isArray(value)) {
      output.push({ path, preview: formatPreviewValue(value), type: 'array' });
      value.slice(0, 3).forEach((item, index) => {
        const arrayPath = `${path}/${index}`;
        output.push({ path: arrayPath, preview: formatPreviewValue(item), type: Array.isArray(item) ? 'array' : typeof item });
        if (isPlainObject(item)) collectPathsFromObject(item, arrayPath, output, depth + 1);
      });
      return;
    }

    output.push({ path, preview: formatPreviewValue(value), type: typeof value });
  });

  return output;
}

function normaliseBodyPath(path) {
  if (isBlank(path)) return '';

  let value = String(path).trim();

  // The data selector is for body fields only, so keep values as body-relative paths.
  // Examples:
  // body.ldc1612_ch1_sweep.2.raw -> ldc1612_ch1_sweep/2/raw
  // body/ldc1612_ch1_sweep/2/raw -> ldc1612_ch1_sweep/2/raw
  if (value.startsWith('body.')) value = value.slice(5);
  if (value.startsWith('body/')) value = value.slice(5);

  // Do not offer metadata/header paths in the dataId selector.
  if (
    value === 'body' ||
    value.startsWith('headers.') ||
    value.startsWith('headers/') ||
    value.startsWith('header.') ||
    value.startsWith('header/') ||
    value.startsWith('date') ||
    value.startsWith('created') ||
    value.startsWith('updated') ||
    value === 'id' ||
    value === 'DeviceId' ||
    value === 'deviceId'
  ) {
    return '';
  }

  // Store widget paths with slash separators.
  return value
    .replace(/\.+/g, '/')
    .replace(/\/+/g, '/')
    .replace(/^\/+|\/+$/g, '');
}

function makeDataPathOptions(DeviceData = [], availableDataIds = []) {
  const map = new Map();

  asArray(availableDataIds).forEach((item) => {
    const rawPath = typeof item === 'string' ? item : item?.path || item?.id || item?.value;
    const path = normaliseBodyPath(rawPath);

    if (!isBlank(path)) {
      map.set(path, {
        path,
        label: path,
        preview: typeof item === 'object' ? item.preview || item.label || '' : '',
        source: 'Provided body field',
      });
    }
  });

  asArray(DeviceData)
    .slice(0, 10)
    .forEach((entry, entryIndex) => {
      // Only collect fields inside body.
      // This intentionally excludes dateCreated, headers, id, device metadata, and the body. prefix.
      const bodyPaths = collectPathsFromObject(entry?.body || {});

      bodyPaths.forEach((item) => {
        const path = normaliseBodyPath(item.path);

        if (!isBlank(path) && !map.has(path)) {
          map.set(path, {
            ...item,
            path,
            label: path,
            source: entryIndex === 0 ? 'Latest body' : `Body ${entryIndex + 1}`,
          });
        }
      });
    });

  return Array.from(map.values()).sort((a, b) => a.path.localeCompare(b.path));
}

function isDataPathInput(element = {}) {
  const id = String(element.id || '').toLowerCase();
  const name = String(element.name || element.label || '').toLowerCase();

  return (
    element.dataPath === true ||
    id === 'dataid' ||
    id === 'data_id' ||
    id === 'path' ||
    id === 'id' ||
    id === 'lat' ||
    id === 'lng' ||
    id === 'long' ||
    id === 'latitude' ||
    id === 'longitude' ||
    id === 'datepath' ||
    name.includes('data id') ||
    name.includes('dataid') ||
    name.includes('id path') ||
    name.includes('path')
  );
}

function getFieldValue(selectedDetails = {}, element = {}) {
  if (hasOwn(selectedDetails, element.id)) return selectedDetails[element.id] ?? '';
  return element.default ?? '';
}

function makeDefaultSettings(settings = []) {
  return asArray(settings).reduce((acc, element) => {
    if (!element?.id) return acc;

    if (element.type === 'switch' || element.type === 'checkbox') {
      acc[element.id] = element.default === true;
    } else if (element.type === 'array') {
      acc[element.id] = [];
    } else {
      acc[element.id] = element.default ?? '';
    }

    return acc;
  }, {});
}

function getAddLabel(element = {}) {
  if (element.addLabel) return element.addLabel;

  const id = String(element.id || '').toLowerCase();
  if (id === 'datasets') return 'Add dataset';
  if (id === 'columns') return 'Add column';
  if (id === 'fields') return 'Add field';

  const name = element.name || element.label || element.id || 'item';
  return `Add ${String(name).replace(/s$/, '')}`;
}

function TabPanel({ children, value, index, ...other }) {
  return (
    <Box
      role="tabpanel"
      hidden={value !== index}
      id={`widget-settings-tabpanel-${index}`}
      aria-labelledby={`widget-settings-tab-${index}`}
      {...other}
    >
      {value === index ? <Box sx={{ pt: 2 }}>{children}</Box> : null}
    </Box>
  );
}

function TabBar({ children, SelecedDetails = {}, handleDetailsChange }) {
  const [tabValue, setTabValue] = React.useState(0);
  const widgetId = SelecedDetails?.widget || null;
  const widgetSettings = widgetId ? Widgets[widgetId]?.Settings : null;

  // When the selected widget changes, always return to the settings tab.
  // This makes a newly-added widget show its settings straight away.
  React.useEffect(() => {
    setTabValue(0);
  }, [SelecedDetails?.id, widgetId]);

  const handleGridChange = (event) => {
    handleDetailsChange(null, 'md', event.target.value);
  };

  if (widgetSettings?.options?.grid === false) {
    return <Box>{children}</Box>;
  }

  return (
    <Box>
      <Tabs
        value={tabValue}
        onChange={(event, newValue) => setTabValue(newValue)}
        indicatorColor="primary"
        textColor="primary"
        variant="scrollable"
        scrollButtons="auto"
        aria-label="Widget settings tabs"
      >
        <Tab label="Settings" />
        <Tab label="Grid" />
      </Tabs>

      <TabPanel value={tabValue} index={0}>
        {children}
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        <Stack spacing={1.5}>
          <Typography variant="body2">Select the box width.</Typography>
          <Select
            labelId="widget-grid-width-label"
            id="widget-grid-width"
            value={SelecedDetails?.md ?? 12}
            name="md"
            onChange={handleGridChange}
            fullWidth
          >
            <MenuItem value={12}>Full width</MenuItem>
            <MenuItem value={6}>Half width</MenuItem>
            <MenuItem value={4}>Third width</MenuItem>
            <MenuItem value={3}>Quarter width</MenuItem>
            <Divider />
            <MenuItem value={8}>2/3 width</MenuItem>
            <MenuItem value={9}>3/4 width</MenuItem>
          </Select>
        </Stack>
      </TabPanel>
    </Box>
  );
}

function DataPathField({ element, selectedDetails, handleDetailsChange, dataPathOptions }) {
  const value = getFieldValue(selectedDetails, element);
  const label = element.name || element.label || element.id;
  const selectedOption = dataPathOptions.find((option) => option.path === value) || null;

  return (
    <Autocomplete
      freeSolo
      options={dataPathOptions}
      value={selectedOption}
      inputValue={value || ''}
      onInputChange={(event, newInputValue) => {
        handleDetailsChange(null, element.id, newInputValue ?? '');
      }}
      onChange={(event, newValue) => {
        if (typeof newValue === 'string') {
          handleDetailsChange(null, element.id, newValue);
          return;
        }

        handleDetailsChange(null, element.id, newValue?.path || '');
      }}
      getOptionLabel={(option) => {
        if (typeof option === 'string') return option;
        return option?.path || '';
      }}
      isOptionEqualToValue={(option, currentValue) => option.path === currentValue?.path}
      renderOption={(props, option) => (
        <Box component="li" {...props} key={option.path}>
          <Stack spacing={0.25} sx={{ minWidth: 0 }}>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
              <Typography variant="body2" sx={{ fontWeight: 700 }}>{option.path}</Typography>
              {option.type ? <Chip size="small" label={option.type} /> : null}
              {option.source ? <Chip size="small" variant="outlined" label={option.source} /> : null}
            </Stack>
            {!isBlank(option.preview) ? (
              <Typography variant="caption" color="text.secondary" noWrap>
                {option.preview}
              </Typography>
            ) : null}
          </Stack>
        </Box>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          fullWidth
          label={label}
          placeholder={element.placeholder || 'Type or select a body path'}
          variant="standard"
          helperText={
            dataPathOptions.length
              ? 'Select from recent body data or type a custom body path using / separators.'
              : 'Type a body path. Pass DeviceData into WidgetsSettings to show recent body fields.'
          }
        />
      )}
    />
  );
}

function RenderFormInput({
  element,
  selectedDetails,
  handleDetailsChange,
  handleDetailsArrayChange,
  handleDetailsArrayAdd,
  handleDetailsArrayRemove,
  dataPathOptions,
}) {
  if (!element?.type) return null;

  const label = element.name || element.label || element.id;
  const value = getFieldValue(selectedDetails, element);

  const handleCheckedChange = (event) => {
    handleDetailsChange(null, event.target.name, event.target.checked);
  };

  if (isDataPathInput(element) && element.type === 'text') {
    return (
      <DataPathField
        element={element}
        selectedDetails={selectedDetails}
        handleDetailsChange={handleDetailsChange}
        dataPathOptions={dataPathOptions}
      />
    );
  }

  switch (element.type) {
    case 'switch':
      return (
        <FormControlLabel
          control={<Switch onChange={handleCheckedChange} name={element.id} checked={value === true} />}
          label={label}
        />
      );

    case 'checkbox':
      return (
        <FormControlLabel
          control={<Checkbox onChange={handleCheckedChange} name={element.id} checked={value === true} />}
          label={label}
        />
      );

    case 'array': {
      const rows = asArray(selectedDetails[element.id]);
      const addLabel = getAddLabel(element);

      return (
        <Stack spacing={1.5}>
          {rows.length ? (
            <Stack spacing={1.5}>
              {rows.map((arrayDetails, index) => (
                <Paper key={`${element.id}-${index}`} variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                  <Stack spacing={1.5}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                      <Typography variant="subtitle2">
                        {element.itemLabel || label || 'Item'} {index + 1}
                      </Typography>
                      <Tooltip title="Remove">
                        <IconButton
                          size="small"
                          color="error"
                          aria-label="Remove item"
                          onClick={(event) => handleDetailsArrayRemove(event, element.id, index)}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>

                    <Inputs
                      dataPathOptions={dataPathOptions}
                      handleDetailsChange={(event, name = null, nextValue = null) =>
                        handleDetailsArrayChange(event, index, name, nextValue, element.id)
                      }
                      selectedDetails={arrayDetails || {}}
                      settings={element.settings}
                    />
                  </Stack>
                </Paper>
              ))}
            </Stack>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No {String(label || 'items').toLowerCase()} added yet.
            </Typography>
          )}

          <Button
            variant="contained"
            startIcon={<AddCircleOutlineIcon />}
            onClick={(event) => handleDetailsArrayAdd(event, element.id, element.settings)}
            sx={{ alignSelf: 'flex-start', borderRadius: 999 }}
          >
            {addLabel}
          </Button>
        </Stack>
      );
    }

    case 'html':
      return typeof element.html === 'string' ? <Box>{element.html}</Box> : element.html || null;

    case 'textarea':
      return (
        <Box>
          <Typography variant="body2" sx={{ mb: 0.5 }}>{label}</Typography>
          <TextareaAutosize
            minRows={3}
            style={{ width: '100%', boxSizing: 'border-box' }}
            name={element.id}
            value={value}
            onChange={handleDetailsChange}
          />
        </Box>
      );

    case 'number': {
      const inputProps = {};
      if ('max' in element) inputProps.max = element.max;
      if ('min' in element) inputProps.min = element.min;
      if ('step' in element) inputProps.step = element.step;

      return (
        <TextField
          fullWidth
          type="number"
          inputProps={inputProps}
          name={element.id}
          value={value}
          onChange={handleDetailsChange}
          label={label}
          variant="standard"
        />
      );
    }

    case 'select':
      return (
        <FormControl fullWidth variant="standard">
          <InputLabel>{label}</InputLabel>
          <Select name={element.id} value={value} onChange={handleDetailsChange} label={label}>
            {asArray(element.options).map((option) => {
              const optionValue = typeof option === 'object' ? option.value : option;
              const optionLabel = typeof option === 'object' ? option.label : option;
              return (
                <MenuItem key={optionValue} value={optionValue}>
                  {optionLabel}
                </MenuItem>
              );
            })}
          </Select>
        </FormControl>
      );

    case 'date':
    case 'text':
    case 'color':
    default:
      return (
        <TextField
          fullWidth
          type={element.type === 'color' ? 'color' : element.type === 'date' ? 'date' : 'text'}
          name={element.id}
          value={value}
          onChange={handleDetailsChange}
          label={label}
          placeholder={element.placeholder || ''}
          variant="standard"
          InputLabelProps={element.type === 'date' ? { shrink: true } : undefined}
        />
      );
  }
}

function Inputs({ selectedDetails = {}, SelecedDetails, settings = [], handleDetailsChange, dataPathOptions = [] }) {
  const details = selectedDetails || SelecedDetails || {};

  const handleDetailsArrayChange = (event, index, name = null, value = null, arrayId = null) => {
    const currentRows = asArray(details[arrayId]);
    const nextRows = [...currentRows];
    const currentRow = nextRows[index] || {};

    if (name) {
      nextRows[index] = { ...currentRow, [name]: value };
    } else {
      nextRows[index] = { ...currentRow, [event.target.name]: event.target.value };
    }

    handleDetailsChange(null, arrayId, nextRows);
  };

  const handleDetailsArrayAdd = (event, name = null, childSettings = []) => {
    const currentRows = asArray(details[name]);
    const nextRow = makeDefaultSettings(childSettings);
    handleDetailsChange(null, name, [...currentRows, nextRow]);
  };

  const handleDetailsArrayRemove = (event, name = null, index = null) => {
    const nextRows = [...asArray(details[name])];
    nextRows.splice(index, 1);
    handleDetailsChange(null, name, nextRows);
  };

  return (
    <Grid container spacing={2}>
      {asArray(settings).map((element, index) => {
        if (!element?.type) return null;

        return (
          <Grid
            item
            key={element.id || `${element.type}-${index}`}
            xs={element.size?.xs ?? 12}
            sm={element.size?.sm ?? 12}
            md={element.size?.md ?? 12}
            lg={element.size?.lg ?? 12}
            xl={element.size?.xl ?? 12}
          >
            <RenderFormInput
              element={element}
              selectedDetails={details}
              handleDetailsChange={handleDetailsChange}
              handleDetailsArrayChange={handleDetailsArrayChange}
              handleDetailsArrayAdd={handleDetailsArrayAdd}
              handleDetailsArrayRemove={handleDetailsArrayRemove}
              dataPathOptions={dataPathOptions}
            />
          </Grid>
        );
      })}
    </Grid>
  );
}

function View({
  SelecedDetails,
  SelectedDetails,
  handleDetailsChange,
  DeviceData = [],
  availableDataIds = [],
  AvailableDataIds = [],
}) {
  const selectedDetails = SelecedDetails || SelectedDetails || {};
  const widgetId = selectedDetails?.widget || null;
  const widget = widgetId ? Widgets[widgetId] : null;
  const widgetSettings = widget?.Settings;

  const dataPathOptions = React.useMemo(
    () => makeDataPathOptions(DeviceData, [...asArray(availableDataIds), ...asArray(AvailableDataIds)]),
    [DeviceData, availableDataIds, AvailableDataIds]
  );

  const handleNameChange = handleDetailsChange || (() => {});

  if (!widgetId) {
    return (
      <TabBar SelecedDetails={selectedDetails} handleDetailsChange={handleNameChange}>
        <Box sx={{ py: 1 }}>
          <TextField
            name="name"
            value={selectedDetails.name ?? ''}
            onChange={handleNameChange}
            label="Block name"
            variant="standard"
            fullWidth
          />
        </Box>
      </TabBar>
    );
  }

  if (!widgetSettings) {
    return (
      <Box sx={{ py: 1 }}>
        <Typography color="error">Unknown widget: {widgetId}</Typography>
      </Box>
    );
  }

  return (
    <TabBar SelecedDetails={selectedDetails} handleDetailsChange={handleNameChange}>
      <Stack spacing={2} sx={{ py: 1 }}>
        <TextField
          name="name"
          fullWidth
          value={selectedDetails.name ?? ''}
          onChange={handleNameChange}
          label="Block name"
          variant="standard"
        />

        {selectedDetails.id && widgetSettings?.options?.view ? <Box>{widgetSettings.options.view}</Box> : null}

        <Inputs
          selectedDetails={selectedDetails}
          settings={widgetSettings?.options?.settings || []}
          handleDetailsChange={handleNameChange}
          dataPathOptions={dataPathOptions}
        />
      </Stack>
    </TabBar>
  );
}

export default View;
